class BannerVideo {
	constructor(el) {
		this.el = el;
		this.videoEl = this.el.querySelector('.banner__cover-video');
		this.embedEl = this.el.querySelector('.banner__cover-video-embed');
		this.youtubeEl = this.el.querySelector('.banner__cover-video-youtube');
		this.bannerParentEl = this.el.closest('.banner');
		this.currentSize = '';
		this.youtubePlayer = null;
		this.youtubeApiReady = null;
		this.youtubeRevealTimeout = null;
		this.youtubeRevealDelay = 0;

		// choose initial video and poster
		this.chooseVideoAndPoster();

		// catch playback errors
		this.videoEl.addEventListener('error', this.catchPlaybackError);
		this.videoEl.addEventListener('abort', this.catchPlaybackError);

		// load a new video on resize if needed
		window.addEventListener('resize', () => this.chooseVideoAndPoster());
	}

	setVideoAutoplaySupport(isSupported, removeEl = false) {
		const htmlEl = document.documentElement;

		if (isSupported) {
			htmlEl.classList.add('video-autoplay-support');
			htmlEl.classList.remove('no-video-autoplay-support');
		} else {
			htmlEl.classList.remove('video-autoplay-support');
			htmlEl.classList.add('no-video-autoplay-support');
		}

		if (removeEl) {
			this.el.remove();
		}
	}

	catchPlaybackError = () => {
		// NOTE: Safari will report 'stalled' even when the video is not stalled
		// do not listen for this event
		this.setVideoAutoplaySupport(false, false);
	};

	loadYoutubeApi() {
		if (window.YT && window.YT.Player) {
			return Promise.resolve(window.YT);
		}

		if (this.youtubeApiReady) {
			return this.youtubeApiReady;
		}

		this.youtubeApiReady = new Promise((resolve) => {
			const previousCallback = window.onYouTubeIframeAPIReady;

			window.onYouTubeIframeAPIReady = () => {
				if (typeof previousCallback === 'function') {
					previousCallback();
				}

				resolve(window.YT);
			};

			if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
				const script = document.createElement('script');

				script.src = 'https://www.youtube.com/iframe_api';
				document.head.appendChild(script);
			}
		});

		return this.youtubeApiReady;
	}

	setupYoutubePlayer(videoId) {
		if (!videoId) {
			return;
		}

		this.hideYoutubePlayer();

		this.loadYoutubeApi().then((YT) => {
			if (this.youtubePlayer) {
				this.youtubePlayer.destroy();
			}

			const youtubeTarget = document.createElement('div');

			this.youtubeEl.innerHTML = '';
			this.youtubeEl.appendChild(youtubeTarget);

			this.youtubePlayer = new YT.Player(youtubeTarget, {
				videoId,
				playerVars: {
					autoplay: 1,
					controls: 0,
					disablekb: 1,
					fs: 0,
					iv_load_policy: 3,
					modestbranding: 1,
					mute: 1,
					playsinline: 1,
					rel: 0,
					showinfo: 0
				},
				events: {
					onReady: (event) => {
						event.target.mute();
					},
					onStateChange: (event) => {
						if (event.data === YT.PlayerState.ENDED) {
							event.target.seekTo(0);
							event.target.playVideo();
						}
					}
				}
			});

			this.youtubeRevealTimeout = setTimeout(() => {
				this.youtubeEl.classList.remove('banner__cover-video-youtube--hidden');
			}, this.youtubeRevealDelay);
		});
	}

	hideYoutubePlayer() {
		if (this.youtubeRevealTimeout) {
			clearTimeout(this.youtubeRevealTimeout);
			this.youtubeRevealTimeout = null;
		}

		this.youtubeEl.classList.add('banner__cover-video-youtube--hidden');
	}

	chooseVideoAndPoster = () => {
		const bannerWidth = window.innerWidth;
		let newSize = '';

		// figure out which device size we're looking at
		if (bannerWidth >= 1024) {
			newSize = 'desktop';
		} else if (bannerWidth >= 641) {
			newSize = 'tablet';
		} else {
			newSize = 'mobile';
		}

		// no need to make changes if the device size hasn't changed
		if (this.currentSize === newSize) {
			return;
		}

		this.currentSize = newSize;

		// select the video and poster URLs to use
		let videoUrl = '';
		let videoType = '';
		let videoProvider = '';
		let videoId = '';
		let embedUrl = '';
		let posterUrl = '';

		switch (this.currentSize) {
			case 'desktop':
				videoUrl = this.el.getAttribute('data-video-desktop');
				videoType = this.el.getAttribute('data-video-desktop-type');
				videoProvider = this.el.getAttribute('data-video-desktop-provider');
				videoId = this.el.getAttribute('data-video-desktop-id');
				embedUrl = this.el.getAttribute('data-video-desktop-embed');
				posterUrl = this.el.getAttribute('data-poster-desktop');
				break;

			case 'tablet':
				videoUrl = this.el.getAttribute('data-video-tablet');
				videoType = this.el.getAttribute('data-video-tablet-type');
				videoProvider = this.el.getAttribute('data-video-tablet-provider');
				videoId = this.el.getAttribute('data-video-tablet-id');
				embedUrl = this.el.getAttribute('data-video-tablet-embed');
				posterUrl = this.el.getAttribute('data-poster-tablet');
				break;

			case 'mobile':
				videoUrl = this.el.getAttribute('data-video-mobile');
				videoType = this.el.getAttribute('data-video-mobile-type');
				videoProvider = this.el.getAttribute('data-video-mobile-provider');
				videoId = this.el.getAttribute('data-video-mobile-id');
				embedUrl = this.el.getAttribute('data-video-mobile-embed');
				posterUrl = this.el.getAttribute('data-poster-mobile');
				break;

			default:
				break;
		}

		if (videoType === 'embed' && embedUrl) {
			this.videoEl.pause();
			this.videoEl.removeAttribute('src');
			this.videoEl.hidden = true;

			this.el.classList.add('banner__cover-video-container--embed');
			if (videoProvider === 'youtube') {
				this.embedEl.removeAttribute('src');
				this.embedEl.hidden = true;
				this.youtubeEl.hidden = false;
				this.setupYoutubePlayer(videoId);
			} else {
				this.hideYoutubePlayer();
				this.youtubeEl.hidden = true;
				this.embedEl.setAttribute('src', embedUrl);
				this.embedEl.hidden = false;
			}
			this.setVideoAutoplaySupport(true);
		} else if (videoType === 'file' && videoUrl) {
			// pause the current video, set/load the new URL, and begin playback
			if (this.youtubePlayer) {
				this.youtubePlayer.destroy();
				this.youtubePlayer = null;
			}

			this.el.classList.remove('banner__cover-video-container--embed');
			this.hideYoutubePlayer();
			this.embedEl.removeAttribute('src');
			this.embedEl.hidden = true;
			this.youtubeEl.hidden = true;

			this.videoEl.pause();
			this.videoEl.hidden = false;
			this.videoEl.setAttribute('src', videoUrl);
			this.videoEl.setAttribute('poster', posterUrl);

			this.videoEl.load();
			this.videoEl.play();
			this.setVideoAutoplaySupport(true);
		} else {
			// fallback to banner image for this size if no video URL is set
			this.setVideoAutoplaySupport(false);
		}
	};
}

export default BannerVideo;
